<script setup>
import axios from 'axios';
import { onMounted, ref, watch } from 'vue';
import { finishActivation } from '../../../../helpers/activation';
import { validateSpecialCharacter } from '../../../../helpers/helper';
import PgeButton from '../../../../pge-components/pge-button.vue';
import PgeInputText from '../../../../pge-components/pge-input-text.vue';
import usePasswordMatch, { PASSWORD_HINT, meetsPasswordRule } from '../../../../composables/usePasswordMatch';
import AccessBlockedNotice from '../AccessBlockedNotice.vue';
import AccessRequestPendingNotice from '../AccessRequestPendingNotice.vue';
import RequestAccessModal from '../RequestAccessModal.vue';

const props = defineProps({
    eventData: Object,
    changeFormStep: Function,
    currentFormStepData: {
        type: Object,
        default: () => ({}),
    },
});

const isLoading = ref(false);
const allowSubmit = ref(false);
const errors = ref([]);
const accessChecking = ref(true);
const accessBlocked = ref(false);
const pendingRequest = ref(false);
const requestAccessOpen = ref(false);
const firstnameError = ref(false);
const lastnameError = ref(false);
const hasCustomFields = !!(props.eventData?.settings?.fields && props.eventData.settings.fields.length);

const userFormData = ref({
    email: props.currentFormStepData.email || '',
    firstname: props.currentFormStepData.firstName || null,
    lastname: props.currentFormStepData.lastName || null,
    password: '',
    rePassword: '',
});

// The button stays grey until the two agree; this is what says why.
const { mismatch, onConfirmationFocus } = usePasswordMatch(
    () => userFormData.value.password,
    () => userFormData.value.rePassword,
);

function validateForm(v) {
    let ok = true;
    firstnameError.value = false;
    lastnameError.value = false;

    if (!v.firstname) {
        ok = false;
        if (v.firstname !== null) firstnameError.value = 'Cannot be blank';
    } else if (v.firstname.length > 50) {
        ok = false;
        firstnameError.value = 'Character limit for First Name should be 50 max';
    } else if (validateSpecialCharacter(v.firstname)) {
        ok = false;
        firstnameError.value = "Special characters aren't allowed";
    }

    if (!v.lastname) {
        ok = false;
        if (v.lastname !== null) lastnameError.value = 'Cannot be blank';
    } else if (v.lastname.length > 50) {
        ok = false;
        lastnameError.value = 'Character limit for Last Name should be 50 max';
    } else if (validateSpecialCharacter(v.lastname)) {
        ok = false;
        lastnameError.value = "Special characters aren't allowed";
    }

    if (!(meetsPasswordRule(v.password) && v.password === v.rePassword)) {
        ok = false;
    }

    allowSubmit.value = ok;
}

watch(userFormData, validateForm, { deep: true });

async function handleSubmit() {
    if (!allowSubmit.value) return;
    isLoading.value = true;
    errors.value = [];

    try {
        const res = await axios.post('/api/sso/api/set-password', {
            first_name: userFormData.value.firstname,
            last_name: userFormData.value.lastname,
            email: userFormData.value.email,
            password: userFormData.value.password,
            token: props.currentFormStepData.activationToken,
        });

        if (res.data && res.data.success === false) {
            errors.value = [res.data.message || 'Could not set your password. The link may have expired.'];
            return;
        }

        // The hub marks the member registered on their first sign-in (XN-4827).

        // set-password returns the id only once it has accepted the token. An older
        // SSO omits it, in which case skip the custom fields rather than strand the
        // invitee on a form whose answers could not be kept.
        const ssoUserId = res.data?.data?.id;

        if (hasCustomFields && ssoUserId) {
            props.changeFormStep('custom-fields', {
                ...props.currentFormStepData,
                user: {
                    id: ssoUserId,
                    sso_user_id: ssoUserId,
                    email: userFormData.value.email,
                    first_name: userFormData.value.firstname,
                    last_name: userFormData.value.lastname,
                },
                fromActivation: true,
            }, false);
            return;
        }

        await finishActivation(userFormData.value.email, props.changeFormStep, props.currentFormStepData);
    } catch (error) {
        const data = error?.response?.data;
        if (data?.errors) {
            errors.value = Array.isArray(data.errors) ? data.errors : Object.values(data.errors).flat();
        } else {
            errors.value = [data?.message || 'Could not set your password. The link may have expired or is invalid.'];
        }
    } finally {
        isLoading.value = false;
    }
}

// The activation link skips CheckEmail, which is the only other place the Hub
// access list is pre-screened, so a revoked invitee would otherwise still reach
// a working registration form and never be offered Request Access.
onMounted(async () => {
    validateForm(userFormData.value);

    try {
        const { data } = await axios.post('/api/access/validate-email', { email: userFormData.value.email });
        if (data?.data?.pending_request) {
            pendingRequest.value = true;
        } else if (data?.data?.allowed === false) {
            accessBlocked.value = true;
        }
    } catch (e) {
        // Safe to proceed: the post-login gate is authoritative.
        console.error('validate-email check failed', e);
    } finally {
        accessChecking.value = false;
    }
});
</script>

<template>
    <div class="form">
        <AccessRequestPendingNotice v-if="pendingRequest" />

        <AccessBlockedNotice v-else-if="accessBlocked" @request="requestAccessOpen = true" />

        <template v-else-if="!accessChecking">
        <div class="form__title">Complete your registration to join {{ eventData.settings.event_name }}.</div>
        <div class="bg-white form__inline">
            <pge-input-text
                type="text" name="firstname" label="First Name"
                :model="userFormData.firstname" v-model="userFormData.firstname"
                required has-required-sign no-tabindex
                :should-show-error="firstnameError" :error-message="firstnameError" :has-error="firstnameError ? true : false"
            />
            <pge-input-text
                type="text" name="lastname" label="Last Name"
                :model="userFormData.lastname" v-model="userFormData.lastname"
                required has-required-sign no-tabindex
                :should-show-error="lastnameError" :error-message="lastnameError" :has-error="lastnameError ? true : false"
            />
        </div>
        <div class="bg-white form__inline">
            <pge-input-text type="password" name="password" label="Password" :model="userFormData.password"
                v-model="userFormData.password" required has-required-sign has-trailing-icon no-tabindex
                min-length="12" :should-show-error="!!userFormData.password && !meetsPasswordRule(userFormData.password)" :error-message="PASSWORD_HINT" />
            <pge-input-text type="password" name="rePassword" label="Confirm Password" :model="userFormData.rePassword"
                v-model="userFormData.rePassword" required has-required-sign has-trailing-icon no-tabindex
                :has-error="!!mismatch" :error-message="mismatch"
                @update:focused="onConfirmationFocus" />
        </div>
        <div class="bg-white">
            <pge-input-text type="email" name="email" label="Email" :model="userFormData.email" disabled
                required has-required-sign no-tabindex />
        </div>
        <div class="form__actions">
            <pge-button label="Complete Registration" @click="handleSubmit" :disabled="!allowSubmit || isLoading" />
        </div>
        <div class="form__errors" v-if="errors.length">
            <div v-for="error in errors" :key="error">{{ error }}</div>
        </div>
        </template>

        <RequestAccessModal
            :visible="requestAccessOpen"
            :initial-email="userFormData.email || ''"
            :event-data="eventData"
            @close="requestAccessOpen = false"
        />
    </div>
</template>

<style lang="scss" scoped>
.form {
    display: flex;
    flex-direction: column;
    gap: 22px;

    &__title {
        color: #000 !important;
        font-feature-settings: 'liga' off, 'clig' off !important;
        font-family: Lato, sans-serif !important;
        font-size: 16px !important;
        font-style: normal !important;
        font-weight: 700 !important;
        line-height: normal !important;
        text-align: center;
    }

    &__inline {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 15px;
    }

    &__actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
    }

    &__errors {
        display: flex;
        flex-direction: column;
        gap: 10px;

        div {
            text-align: center;
            color: #f83c3c;
            font-weight: 700;
            font-size: 14px;
            line-height: 20px;
        }
    }
}
</style>
